/**
 * LB-12 Builder B (PR Gabriel backlog) — Redux state for the CRM leads list
 * page that hosts the Kanban funnel + bulk operations toolbar.
 *
 * Responsibilities:
 *   - Leads fetched into stage buckets (Kanban shape)
 *   - Multi-select state (which lead ids are checked)
 *   - Filters (search + status + source + assignedAgent + priority)
 *   - Pagination (page/limit per Kanban column or in list view)
 *   - View mode (kanban | list)
 *   - Async thunks for fetch + bulk operations
 *
 * The Kanban "22 stages" are loaded from the backend (`funnel-slice`
 * fetchFunnelStages) — this slice does NOT hardcode them. Each card carries
 * `stage` + `subStage` per CRM-1 entity columns added 2026-06-02.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { leadsApi, crmBulkApi } from '../api/crm-api';
import type {
  LeadEntity,
  LeadFilterDTO,
  LeadStatus,
  LeadSource,
  Priority,
} from '../types/crm-types';

// ─── Types ──────────────────────────────────────────────────────────────────

export type LeadsViewMode = 'kanban' | 'list';

export interface LeadsListFilters {
  search?: string;
  status?: LeadStatus;
  /** GHL 7.2 — etapa guardada (lead.stage, ver leads-masivo.ts ETAPAS_LEAD) */
  stage?: string;
  source?: LeadSource;
  assignedAgentId?: string;
  priority?: Priority;
  tags?: string[];
}

interface LeadsListState {
  // Data
  leads: LeadEntity[];
  total: number;

  // Selection (lead ids checked for bulk operations)
  selectedIds: string[];

  // Filters + pagination
  filters: LeadsListFilters;
  page: number;
  limit: number;

  // View
  viewMode: LeadsViewMode;

  // Status
  loading: boolean;
  bulkPending: boolean;
  bulkError: string | null;
  error: string | null;
}

const initialState: LeadsListState = {
  leads: [],
  total: 0,
  selectedIds: [],
  filters: {},
  page: 1,
  limit: 50,
  viewMode: 'kanban',
  loading: false,
  bulkPending: false,
  bulkError: null,
  error: null,
};

// ─── Async thunks ───────────────────────────────────────────────────────────

export const fetchLeadsForList = createAsyncThunk(
  'leadsList/fetch',
  async (
    args: { filters?: LeadFilterDTO; page?: number; limit?: number } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await leadsApi.getAll({
        ...args?.filters,
        page: args?.page ?? 1,
        limit: args?.limit ?? 50,
      });
      return response;
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || 'Error fetching leads');
    }
  }
);

export const bulkAssignLeads = createAsyncThunk(
  'leadsList/bulkAssign',
  async (args: { leadIds: string[]; agentId: string }, { rejectWithValue }) => {
    try {
      return await crmBulkApi.assignMultipleLeads(args.leadIds, args.agentId);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || 'Error assigning leads');
    }
  }
);

export const bulkUpdateLeads = createAsyncThunk(
  'leadsList/bulkUpdate',
  async (args: { leadIds: string[]; updates: any }, { rejectWithValue }) => {
    try {
      return await crmBulkApi.updateMultipleLeads(args.leadIds, args.updates);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No se pudieron actualizar los leads');
    }
  }
);

export const bulkTagLeads = createAsyncThunk(
  'leadsList/bulkTag',
  async (args: { leadIds: string[]; tagIds: string[] }, { rejectWithValue }) => {
    try {
      return await crmBulkApi.tagMultipleLeads(args.leadIds, args.tagIds);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No se pudo etiquetar');
    }
  }
);

export const bulkDeleteLeads = createAsyncThunk(
  'leadsList/bulkDelete',
  async (args: { leadIds: string[] }, { rejectWithValue }) => {
    try {
      return await crmBulkApi.deleteMultipleLeads(args.leadIds);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || 'Error deleting leads');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────

const leadsListSlice = createSlice({
  name: 'leadsList',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<LeadsListFilters>) {
      state.filters = action.payload;
      state.page = 1; // reset to first page on filter change
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setViewMode(state, action: PayloadAction<LeadsViewMode>) {
      state.viewMode = action.payload;
    },
    toggleSelect(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.selectedIds.indexOf(id);
      if (idx >= 0) {
        state.selectedIds.splice(idx, 1);
      } else {
        state.selectedIds.push(id);
      }
    },
    selectAll(state) {
      state.selectedIds = state.leads.map((l) => l.id);
    },
    clearSelection(state) {
      state.selectedIds = [];
    },
    moveLeadStage(
      state,
      action: PayloadAction<{ leadId: string; stage: string; subStage?: string }>
    ) {
      // Optimistic update — saga/thunk will reconcile on server response
      const lead = state.leads.find((l) => l.id === action.payload.leadId);
      if (lead) {
        (lead as any).stage = action.payload.stage;
        if (action.payload.subStage !== undefined) {
          (lead as any).subStage = action.payload.subStage;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadsForList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadsForList.fulfilled, (state, action: any) => {
        state.loading = false;
        const payload = action.payload || {};
        state.leads = payload.data || payload.items || payload.leads || [];
        state.total = payload.total ?? state.leads.length;
      })
      .addCase(fetchLeadsForList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // GHL 7.6 — el error de una acción en lote va en su propio campo: la
      // recarga que sigue (fetch.pending) limpia `error` y lo borraría antes de verse.
      .addCase(bulkAssignLeads.pending, (state) => {
        state.bulkPending = true;
        state.bulkError = null;
      })
      .addCase(bulkAssignLeads.fulfilled, (state) => {
        state.bulkPending = false;
        state.selectedIds = [];
      })
      .addCase(bulkAssignLeads.rejected, (state, action) => {
        state.bulkPending = false;
        state.bulkError = action.payload as string;
      })
      .addCase(bulkUpdateLeads.pending, (state) => {
        state.bulkPending = true;
        state.bulkError = null;
      })
      .addCase(bulkUpdateLeads.fulfilled, (state) => {
        state.bulkPending = false;
        state.selectedIds = [];
      })
      .addCase(bulkUpdateLeads.rejected, (state, action) => {
        state.bulkPending = false;
        state.bulkError = action.payload as string;
      })
      .addCase(bulkTagLeads.pending, (state) => {
        state.bulkPending = true;
        state.bulkError = null;
      })
      .addCase(bulkTagLeads.fulfilled, (state) => {
        state.bulkPending = false;
        state.selectedIds = [];
      })
      .addCase(bulkTagLeads.rejected, (state, action) => {
        state.bulkPending = false;
        state.bulkError = action.payload as string;
      })
      .addCase(bulkDeleteLeads.pending, (state) => {
        state.bulkPending = true;
        state.bulkError = null;
      })
      .addCase(bulkDeleteLeads.fulfilled, (state) => {
        state.bulkPending = false;
        state.selectedIds = [];
      })
      .addCase(bulkDeleteLeads.rejected, (state, action) => {
        state.bulkPending = false;
        state.bulkError = action.payload as string;
      });
  },
});

export const {
  setFilters,
  setPage,
  setViewMode,
  toggleSelect,
  selectAll,
  clearSelection,
  moveLeadStage,
} = leadsListSlice.actions;

export default leadsListSlice.reducer;

// ─── Selectors ──────────────────────────────────────────────────────────────

export const selectLeadsList = (state: any) => state.leadsList?.leads ?? [];
export const selectLeadsListTotal = (state: any) => state.leadsList?.total ?? 0;
export const selectLeadsListLoading = (state: any) => !!state.leadsList?.loading;
export const selectLeadsListError = (state: any) => state.leadsList?.error ?? null;
export const selectLeadsListFilters = (state: any) =>
  state.leadsList?.filters ?? {};
export const selectLeadsListPage = (state: any) => state.leadsList?.page ?? 1;
export const selectLeadsListLimit = (state: any) => state.leadsList?.limit ?? 50;
export const selectLeadsListViewMode = (state: any) =>
  state.leadsList?.viewMode ?? 'kanban';
export const selectSelectedIds = (state: any) =>
  state.leadsList?.selectedIds ?? [];
export const selectBulkPending = (state: any) =>
  !!state.leadsList?.bulkPending;
export const selectBulkError = (state: any): string | null => state.leadsList?.bulkError ?? null;

/**
 * Group leads by funnel stage. The Kanban renders one column per stage,
 * stage labels come from the funnel-slice fetched values (loaded separately).
 */
export const selectLeadsByStage = (state: any): Record<string, LeadEntity[]> => {
  const leads: LeadEntity[] = state.leadsList?.leads ?? [];
  const grouped: Record<string, LeadEntity[]> = {};
  for (const lead of leads) {
    const stage = (lead as any).stage || (lead as any).funnelStage || 'unassigned';
    if (!grouped[stage]) grouped[stage] = [];
    grouped[stage].push(lead);
  }
  return grouped;
};
